import React, { useContext, useEffect, useRef, useState } from 'react'
import { ConfigProvider, Select as AntdSelect } from 'antd'
import cls from 'classnames'
import styled, { ThemeContext } from 'styled-components'

import Button from 'components/lib/DesignSystem/Button'
import Checkbox from 'components/lib/DesignSystem/Checkbox'
import Chip from 'components/lib/DesignSystem/Chip'
import { ChevronDown, Loader } from 'components/lib/DesignSystem/Icons'
import { styledDropdownScrollbar } from 'components/styles/sharedStyledCSS'

import Icon from '../Icon'
import withValidation from '../inputs/withValidation'
import LabelText from '../LabelText'

import Tooltip from './Tooltip'

/**
 * @typedef {Object} CustomTagProps
 * @property {number} tagMaxWidth - Maximum width for tag
 */

/**
 * Custom tag renderer component for Select
 * @param {CustomTagProps} props - The props object
 * @returns {Function} Tag render function
 */
const CustomTag =
  ({ tagMaxWidth }) =>
  ({ label, onClose, className }) =>
    (
      <div className={className}>
        <Chip
          checked
          checkedStyle="selected"
          size="small"
          label={label?.props?.title || label}
          onClose={onClose}
          style={{ 'margin-inline-end': '6px' }}
          maxWidth={tagMaxWidth}
        />
      </div>
    )

/**
 * @typedef {Object} CustomMaxTagPlaceholderProps
 * @property {boolean} showShort - Whether to show shortened version
 * @property {string} tagsRenderStyle - Style of tag rendering ('single' or 'multiple')
 */

/**
 * Custom max tag placeholder renderer component for Select
 * @param {CustomMaxTagPlaceholderProps} props - The props object
 * @returns {Function} Max tag placeholder render function
 */
const CustomMaxTagPlaceholder =
  ({ showShort, tagsRenderStyle }) =>
  (items) =>
    (
      <Chip
        checked
        checkedStyle="selected"
        size="small"
        label={
          tagsRenderStyle === 'single'
            ? `${items.length}${!showShort ? ' selected' : ''}`
            : `${!showShort ? '+' : ''}${items.length}`
        }
      />
    )

/**
 * @typedef {Object} LoadingProps
 * @property {string} className - CSS class name
 * @property {Object} theme - Theme object
 */

/**
 * Loading component for Select
 * @param {LoadingProps} props - The props object
 * @returns {JSX.Element} Loading component
 */
const Loading = ({ className, theme }) => (
  <div className={cls('main-loader', className)} title="Loading...">
    <Loader id="Dropdown_Loading_icon" spin fill={theme.primary} width="20px" height="20px" />
    <div id="Dropdown_LoadingSearch_text" className="main-loader__text">
      Loading...
    </div>
  </div>
)

/**
 * @typedef {Object} SelectOption
 * @property {string|number} value - The option value
 * @property {string} label - The option label
 * @property {string} [key] - The option key
 * @property {string} [icon] - Icon name for the option
 * @property {string} [preLabel] - Pre-label text for the option
 */

/**
 * @typedef {Object} SelectProps
 * @property {string} [dropdownClassName] - Class name for dropdown
 * @property {string} [error] - Error message
 * @property {Object} [style] - Style object
 * @property {string} [className] - Class name
 * @property {boolean} [showSearch=true] - Whether to show search input
 * @property {string} [placeholder] - Placeholder text
 * @property {Function} [onChange] - Change handler function
 * @property {Array<SelectOption>} [options=[]] - Options array
 * @property {Array<Object>} [customOptions] - Custom options array
 * @property {string|Array|Object|number} [value] - Selected value
 * @property {boolean} [disabled=false] - Whether select is disabled
 * @property {Function} [getPopupContainer] - Get popup container function
 * @property {string} [id] - ID attribute
 * @property {string} [labelText] - Label text
 * @property {Object} [popup] - Popup config object
 * @property {boolean} [multiple=false] - Whether multiple selection is allowed
 * @property {boolean} [readOnly=false] - Whether select is read only
 * @property {boolean} [allowClear] - Whether to show clear button
 * @property {boolean} [labelAsValue=false] - Whether to use label as value
 * @property {string} [optionFilterProp='value'] - Option filter prop
 * @property {string} [optionLabelProp='children'] - Option label prop
 * @property {string} [notification] - Notification text
 * @property {string} [name] - Name attribute
 * @property {Function} [onSearch] - Search handler function
 * @property {Function} [onAddItem] - Add item handler function
 * @property {Function} [addItemValidation] - Add item validation function
 * @property {boolean} [addItemLoader=false] - Whether add item is loading
 * @property {string} [addItemError=''] - Add item error message
 * @property {Function} [onLazyLoad] - Lazy load handler function
 * @property {boolean} [lazyLoader=false] - Whether lazy loading is active
 * @property {boolean} [searchLoader=false] - Whether search is loading
 * @property {string} [searchValue] - Search value
 * @property {Function} [onClear] - Clear handler function
 * @property {Function} [onBlur] - Blur handler function
 * @property {string} [AddItemButtonText='Add to list'] - Add item button text
 * @property {string} [AddItemInternalValidationErrorText='Item is not valid'] - Add item validation error text
 * @property {('single'|'multiple')} [tagsRenderStyle='single'] - Tags render style
 * @property {boolean} [backspaceTagRemove=true] - Whether backspace removes tags
 * @property {string} [tooltipText] - Tooltip text
 * @property {boolean} [isOpen] - Whether dropdown is open
 * @property {number} [tagMaxWidth] - Maximum width for tags
 * @property {Function} [onSelect] - Select handler function
 * @property {number} [width] - Width of the select component
 * @property {boolean} [virtual=false] - Whether to use virtual scrolling
 * @property {Function} [onDeselect] - Deselect handler function
 * @property {object} [tooltipChildrenWrapperStyles] - styles for tooltipWrapper
 */

/**
 * Select component with enhanced functionality including search, lazy loading, and custom options
 * @param {SelectProps} props - The component props
 * @returns {JSX.Element} The Select component
 */
const Select = ({
  AddItemButtonText = 'Add to list',
  AddItemInternalValidationErrorText = 'Item is not valid',
  addItemError = '',
  addItemLoader = false,
  addItemValidation,
  allowClear,
  backspaceTagRemove = true,
  className,
  customOptions,
  disabled = false,
  dropdownClassName,
  error,
  style = {},
  showSearch = true,
  placeholder,
  onChange = () => {},
  options = [],
  value,
  getPopupContainer,
  id,
  labelText,
  popup,
  multiple = false,
  readOnly = false,
  labelAsValue = false,
  optionFilterProp = 'value',
  optionLabelProp = 'children',
  notification,
  name,
  onSearch,
  onAddItem,
  onLazyLoad,
  lazyLoader = false,
  searchLoader = false,
  searchValue,
  onClear,
  onBlur,
  tooltipText,
  isOpen,
  tagsRenderStyle = 'single',
  tagMaxWidth,
  onSelect,
  width,
  virtual = false,
  onDeselect,
  tooltipChildrenWrapperStyles = {},
}) => {
  const theme = useContext(ThemeContext)
  const wrapperRef = useRef()
  const addItemButtonRef = useRef()
  const addItemErrorRef = useRef()
  const { Option } = AntdSelect
  const [internalValidationError, setInternalValidationError] = useState(true)
  const [currentSearch, setCurrentSearch] = useState('')
  const [lastItemAdded, setLastItemAdded] = useState(null)
  const [open, setOpen] = useState(isOpen || false)

  useEffect(() => {
    // part of the bug with responsive maxTagCount
    if (wrapperRef.current && multiple && tagsRenderStyle === 'multiple') {
      const searchInput = wrapperRef.current.querySelector('input')

      if (searchInput) {
        searchInput.blur()
      }
    }
  }, [wrapperRef.current])

  const onScrollCheck = ({ target }) => {
    const maxScroll = target.scrollHeight - target.clientHeight * 2

    if (target.scrollTop >= maxScroll && !lazyLoader) {
      onLazyLoad()
    }
  }

  const handleOnSearch = (search) => {
    setCurrentSearch(search)

    if (onSearch) {
      onSearch(search)
    }
  }

  const handleAddItem = () => {
    if (onAddItem) {
      setLastItemAdded(currentSearch)

      const isNotValid = !!addItemValidation && !addItemValidation(currentSearch)
      setInternalValidationError(isNotValid)

      if (!isNotValid) {
        onAddItem(currentSearch)
        setCurrentSearch('')

        // part of the bug with responsive maxTagCount
        if (multiple && tagsRenderStyle === 'multiple') {
          const searchInput = wrapperRef.current.querySelector('input')

          if (searchInput) {
            searchInput.blur()
          }
        }
      }
    }
  }

  // close search panel after new item successfully added to valid options
  useEffect(() => {
    if (lastItemAdded && options.find((i) => i.label === lastItemAdded)) {
      setOpen(false)
      setLastItemAdded(null)
    }
  }, [options, lastItemAdded])

  const notFoundContentComponent = searchLoader ? (
    <Loading theme={theme} />
  ) : onAddItem && currentSearch.trim() ? (
    <>
      {(internalValidationError || addItemError) && currentSearch === lastItemAdded ? (
        <div className="add-item-error" ref={addItemErrorRef}>
          {internalValidationError ? AddItemInternalValidationErrorText : addItemError}
        </div>
      ) : addItemLoader ? (
        <Loading theme={theme} />
      ) : (
        <div ref={addItemButtonRef}>
          <Button
            text={AddItemButtonText}
            styleType="tertiary"
            onClick={handleAddItem}
            className="add-item-button"
            id="Dropdown_AddItem_Button"
          />
        </div>
      )}
    </>
  ) : null

  const currentValue =
    value === '' ? undefined : labelAsValue ? { value: value?.label, label: value?.label } : value

  const handleSelect = (val, option) => {
    if (onSelect) {
      onSelect(val, option)
    }

    const searchableLabel = typeof option?.label === 'string' ? option.label : option.value

    if (searchableLabel?.toLowerCase().includes(currentSearch.toLowerCase())) {
      setCurrentSearch('')
    }
    /*
        This code is to catch specific cases where user clicks twice on same option
        Since normally selectors don't trigger onChange handler for same value, here we do it
        manually to catch clicking twice on same field like "custom range date"
      */
    if (val === currentValue?.value) {
      onChange(val, option)
    }
  }

  const DropdownComponent = (
    <div id={id ? `${id}-container` : undefined}>
      <AntdSelect
        id={id}
        notFoundContent={notFoundContentComponent}
        onPopupScroll={(e) => {
          if (typeof onLazyLoad === 'function') {
            onScrollCheck(e)
          }
        }}
        onDropdownVisibleChange={(visible) => {
          setOpen(visible)

          if (!visible) {
            setCurrentSearch('')
          }
        }}
        onInputKeyDown={(event) => {
          if (event.key === 'Enter' && (addItemButtonRef.current || addItemErrorRef.current)) {
            event.stopPropagation()

            if (addItemButtonRef.current) {
              handleAddItem()
            }
          }
          // prevent deleting tags on multiple select
          if (!backspaceTagRemove && event.key === 'Backspace') {
            event.stopPropagation()
          }
        }}
        allowClear={allowClear !== undefined ? allowClear : multiple}
        status={
          error ||
          (onAddItem &&
            (internalValidationError || addItemError) &&
            currentSearch === lastItemAdded)
            ? 'error'
            : ''
        }
        mode={multiple ? 'multiple' : undefined}
        popupClassName={dropdownClassName}
        style={style}
        showSearch={showSearch || !!onAddItem}
        placeholder={placeholder}
        suffixIcon={<ChevronDown width="20px" height="20px" fill={theme.ds_gray_2} />}
        onChange={onChange}
        onSelect={handleSelect}
        onSearch={handleOnSearch}
        value={currentValue}
        open={open}
        className={cls('is-antd-select', className)}
        virtual={virtual}
        disabled={disabled}
        getPopupContainer={getPopupContainer || (() => wrapperRef.current)}
        tagRender={CustomTag({ tagMaxWidth })}
        maxTagCount={
          multiple && tagsRenderStyle === 'multiple' && !currentSearch ? 'responsive' : 0
        }
        maxTagPlaceholder={CustomMaxTagPlaceholder({ tagsRenderStyle, showShort: !!currentSearch })}
        // there is an antd bug with responsive maxTagCount without focus: https://github.com/ant-design/ant-design/issues/29444
        autoFocus={multiple && tagsRenderStyle === 'multiple' ? true : undefined}
        popupMatchSelectWidth={false}
        dropdownStyle={{ maxWidth: 420, textAlign: 'left' }}
        optionFilterProp={optionFilterProp}
        optionLabelProp={optionLabelProp}
        name={name}
        searchValue={searchValue}
        onClear={onClear}
        onBlur={onBlur}
        onDeselect={onDeselect}
        dropdownRender={(defaultDropdown) => (
          <>{searchLoader ? <Loading theme={theme} /> : defaultDropdown}</>
        )}
      >
        <>
          {customOptions
            ? customOptions.map((el) => (
                <el key={el.key} {...el.props}>
                  {el.props.children}
                </el>
              ))
            : options.map((option) => {
                const optionKey = option.key || `${id}_${option.value}`
                const checkboxId = `${optionKey}_Checkbox`

                return (
                  <Option
                    key={option.key || option.value}
                    label={option.label}
                    value={labelAsValue ? option.label : option.value}
                    data={option}
                  >
                    <div className="option" title={option.label}>
                      {multiple ? (
                        <Checkbox
                          id={checkboxId}
                          preventDefault
                          checked={
                            Array.isArray(value)
                              ? value?.includes(
                                  labelAsValue
                                    ? option.label
                                    : option.value !== null || option.value !== undefined
                                    ? option.value
                                    : option
                                )
                              : false
                          }
                        />
                      ) : null}
                      {option.icon ? (
                        <span className="left_icon">
                          <Icon size="15px" imageName={option.icon} />
                        </span>
                      ) : null}
                      {option.preLabel ? (
                        <span className="pre-label">{option.preLabel}</span>
                      ) : null}
                      <div className="option_label typography-body-1">{option.label}</div>
                    </div>
                  </Option>
                )
              })}
          {lazyLoader && (
            <Option key="lazyLoader">
              <Loading className="lazy-loader" theme={theme} />
            </Option>
          )}
        </>
      </AntdSelect>
    </div>
  )

  return (
    <ConfigProvider
      // properties of token can not be undefined while styl color not immediately loaded
      theme={{
        token: theme.colors.primary
          ? {
              colorPrimary: theme.colors.primary,
              fontFamily: 'Figtree',
            }
          : undefined,
      }}
    >
      <SelectWrapper
        id={`select-wrapper-${id}`}
        ref={wrapperRef}
        width={width}
        disabled={disabled}
        className={cls(className, 'typography-body-1', { readonly: readOnly })}
      >
        {labelText && (
          <LabelText
            text={labelText}
            disabled={disabled}
            notification={notification}
            tooltipText={tooltipText}
            id={id}
          />
        )}
        {popup ? (
          <Tooltip
            overlayStyle={{
              maxWidth: '250px',
              whiteSpace: 'normal',
            }}
            childrenWrapperStyle={{ display: 'flex', ...tooltipChildrenWrapperStyles }}
            text={popup.text}
          >
            {DropdownComponent}
          </Tooltip>
        ) : (
          DropdownComponent
        )}
      </SelectWrapper>
    </ConfigProvider>
  )
}

export default Select

Select.Field = withValidation(Select)

const SelectWrapper = styled.div`
  width: ${({ width }) => (width ? `${width}px` : '100%')};

  .main-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 60px;

    &.lazy-loader {
      height: 18px;
      justify-content: flex-start;
    }

    &__text {
      margin-left: 8px;
      font-size: 14px;
      color: ${({ theme }) => theme.ds_gray_2};
    }
  }

  .ant-select-item-empty {
    .main-loader {
      height: 64px;
    }
  }

  .loading-option {
    pointer-events: none;
    background-color: transparent !important;
  }

  .ant-select:not(.ant-select-disabled):not(.ant-select-status-error):hover .ant-select-selector {
    border-color: ${({ theme, disabled }) => (disabled ? '' : theme.colors.primary)};
  }

  .ant-select {
    width: 100%;
    max-width: unset;
  }

  &.readonly {
    pointer-events: none;

    .ant-select-arrow {
      display: none;
    }
  }

  .ant-select-selection-overflow-item.ant-select-selection-overflow-item-rest {
    .ant-select-selection-item {
      margin: 0;
      padding: 0 !important;
      background: none;
      margin-inline-end: 6px;

      .ant-select-selection-item-content {
        margin-inline-end: 0;
      }
    }
  }

  .ant-select-selection-overflow {
    max-width: calc(100% - 12px);
  }

  .ant-select-selection-overflow-item.ant-select-selection-overflow-item-suffix {
    max-width: calc(100% - 44px);
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    font-weight: ${({ theme }) => theme.typography.weight.regular};
  }

  .ant-select-arrow {
    width: 20px;
    height: 20px;
    margin: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  .ant-select-clear {
    margin-right: 22px;
  }

  .ant-select-selector {
    height: 36px;

    .ant-select-selection-item {
      padding-right: 20px !important;
      text-align: left;
    }
  }

  // disabled
  .ant-select-disabled.ant-select:not(.ant-select-customize-input) {
    .ant-select-selector {
      border: 1px solid ${({ theme }) => theme.ds_gray_7};
      background: #fff;
      color: ${({ theme }) => theme.ds_gray_1};
      cursor: default;
    }

    .ant-select-arrow {
      display: none;
    }
  }

  .rc-virtual-list-scrollbar {
    width: 4px !important;
  }

  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input):not(
      .ant-select-status-error
    ),
  .ant-select:not(.ant-select-customize-input) {
    &.ant-select-open {
      .ant-select-selection-item {
        color: ${({ theme }) => theme.ds_gray_1};
      }
    }

    &.ant-select-focused {
      .ant-select-selector {
        border: 1px solid #2e41ea;
      }
    }

    .ant-select-selector {
      padding: 0 12px;
      border: 1px solid ${({ theme }) => theme.ds_gray_6};
      border-radius: 4px;
      box-shadow: none;
      outline: none;

      &:hover {
        border-color: ${({ theme, disabled }) =>
          !disabled ? theme.colors.primary : theme.ds_gray_6};
      }
    }
  }

  &.partner-companyFilter {
    .ant-select:not(.ant-select-customize-input) {
      &.ant-select-open {
        .ant-select-selection-item {
          color: #ffffff !important;
        }
      }
    }
  }

  .ant-select-selection-search {
    margin: 0;
  }

  .ant-select-single {
    height: 36px;
  }

  .ant-select-single:not(.ant-select-customize-input) {
    .ant-select-selector,
    .ant-select-selection-search,
    .ant-select-selection-search-input,
    .ant-select-selection-placeholder {
      height: 36px;
    }

    .ant-select-selection-placeholder,
    .ant-select-selection-item {
      line-height: 33px;
    }

    .ant-select-selection-placeholder {
      color: ${({ theme }) => theme.ds_gray_4};
    }
  }

  .ant-select-dropdown {
    color: ${({ theme }) => theme.colors.text.primary};
    padding: 8px;
    z-index: 1050 !important;
    ${({ width }) => width && `width: ${width}px`};

    .ant-select-item {
      color: ${({ theme }) => theme.colors.text.primary};
      padding: 7px 8px;
      border-radius: 4px;

      &.ant-select-item-option-selected {
        color: ${({ theme }) => theme.colors.text.primary};
        background-color: ${({ theme, multiple }) =>
          !multiple ? theme.colors.background.dropdown.selected : 'white'};
      }

      &:hover {
        background-color: ${({ theme }) => theme.colors.background.dropdown.hover};
      }

      // has checkbox properties
      &:has(.ant-checkbox-wrapper) {
        &.ant-select-item-option-selected {
          color: ${({ theme }) => theme.colors.text.primary};
          background-color: white !important;
        }
      }
    }
  }

  .ant-select-dropdown .ant-select-item-option-state {
    display: none;
  }

  .ant-select-selection-item {
    .option {
      .option_label {
        display: block;
        white-space: nowrap;
      }
    }
  }

  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.colors.text.hint};
    font-weight: ${({ theme }) => theme.typography.weight.regular};
    font-size: ${({ theme }) => theme.typography.size.body};
    inset-inline-end: 30px;
  }

  .option {
    height: 100%;
    display: flex;
    gap: 12px;
    align-items: center;

    .option_label {
      min-width: 0;
      min-width: 0;
      display: -webkit-box;
      // flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .left_icon {
      display: flex;
      align-items: center;
    }
  }

  .add-item-error {
    display: flex;
    padding: 8px 12px;
    width: 100%;
    justify-content: left;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.negative};
  }

  .add-item-button {
    width: 100%;
    justify-content: left;
    font-size: 12px;
    font-weight: 600;
  }

  .is-button-internal-container .left-icon {
    margin-right: 5px;
  }
  .pre-label {
    background-color: ${({ theme }) => theme.colors.background.dropdown.preLabel};
    color: ${({ theme }) => theme.colors.text.subText};
    padding: 2px 8px 4px;
    width: 38px;
    text-align: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-right: 8px;
    font-size: ${({ theme }) => theme.typography.size.caption};
    line-height: 1;
    &.icon {
      padding: 1.5px 11px;
    }
  }
  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: ${({ theme }) => theme.colors.background.dropdown.hover};
    .pre-label {
      background-color: ${({ theme }) => theme.ds_gray_8};
    }
  }

  * {
    ${styledDropdownScrollbar()}
  }
`
